import ExcelJS from "exceljs";
import dayjs from "dayjs";
import apiService from "../services/api";

const TRADE_TYPE_LABELS = { spot: "现货", swap: "合约" };
const BORROW_STATUS_LABELS = { active: "生效中", repaid: "已归还", overdue: "已逾期" };

const tradeTypeLabel = (tt) => TRADE_TYPE_LABELS[tt] || tt || "";
const borrowStatusLabel = (status) => BORROW_STATUS_LABELS[status] || status || "";
const formatDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "");
const formatDateTime = (value) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "");

/** 借币摘要：USDT: 1000（生效中）；BTC: 0.5（已归还） */
export function buildBorrowSummary(borrows) {
  if (!borrows?.length) return "";
  return borrows
    .map((b) => `${b.asset}: ${b.borrow_amount}（${borrowStatusLabel(b.status)}）`)
    .join("；");
}

/** 按当前筛选条件分页拉取全部账号配置（后端按登录角色限定数据范围） */
export async function fetchAllAccountConfigs(searchParams = {}) {
  const rows = [];
  let page = 1;
  let totalPages = 1;
  do {
    const params = { ...searchParams, page, page_size: 200 };
    Object.keys(params).forEach((key) => {
      const v = params[key];
      if (v === "" || v === undefined || v === null) {
        delete params[key];
      }
    });
    const res = await apiService.accountConfigs.getConfigs(params);
    const payload = res.data?.data || {};
    rows.push(...(payload.list || []));
    totalPages = Number(payload.total_pages || 1);
    page += 1;
  } while (page <= totalPages);
  return rows;
}

function addHeaderRow(ws, headers) {
  const row = ws.addRow(headers);
  row.font = { bold: true };
  return row;
}

function addConfigSheet(wb, configs) {
  const ws = wb.addWorksheet("账号配置");
  ws.columns = [
    { width: 24 },
    { width: 14 },
    { width: 10 },
    { width: 14 },
    { width: 16 },
    { width: 12 },
    { width: 16 },
    { width: 12 },
    { width: 8 },
    { width: 30 },
    { width: 40 },
    { width: 20 },
    { width: 20 },
  ];
  addHeaderRow(ws, [
    "账号",
    "交易所",
    "交易类型",
    "所属用户",
    "业务线",
    "业务线代码",
    "策略类型",
    "策略类型代码",
    "状态",
    "描述",
    "借币摘要",
    "创建时间",
    "更新时间",
  ]);
  for (const cfg of configs) {
    ws.addRow([
      cfg.account || "",
      cfg.exchange || "",
      tradeTypeLabel(cfg.trade_type),
      cfg.owner?.username || "未指定",
      cfg.business_line?.name || "",
      cfg.business_line?.code || "",
      cfg.strategy_type?.name || "",
      cfg.strategy_type?.code || "",
      cfg.is_active ? "启用" : "禁用",
      cfg.description || "",
      buildBorrowSummary(cfg.borrow_configs),
      formatDateTime(cfg.created_at),
      formatDateTime(cfg.updated_at),
    ]);
  }
  if (!configs.length) {
    ws.addRow(["无数据"]);
  }
}

function addBorrowSheet(wb, configs) {
  const ws = wb.addWorksheet("借币配置");
  ws.columns = [
    { width: 24 },
    { width: 14 },
    { width: 10 },
    { width: 10 },
    { width: 16 },
    { width: 14 },
    { width: 10 },
    { width: 14 },
    { width: 10 },
    { width: 30 },
  ];
  addHeaderRow(ws, [
    "账号",
    "交易所",
    "交易类型",
    "资产",
    "借币数量",
    "借币日期",
    "利率（%）",
    "到期日",
    "状态",
    "备注",
  ]);
  let count = 0;
  for (const cfg of configs) {
    for (const borrow of cfg.borrow_configs || []) {
      count += 1;
      ws.addRow([
        cfg.account || "",
        cfg.exchange || "",
        tradeTypeLabel(cfg.trade_type),
        borrow.asset || "",
        borrow.borrow_amount ?? "",
        formatDate(borrow.borrow_date),
        borrow.interest_rate != null ? Number((borrow.interest_rate * 100).toFixed(4)) : "",
        formatDate(borrow.due_date),
        borrowStatusLabel(borrow.status),
        borrow.remark || "",
      ]);
    }
  }
  if (count === 0) {
    ws.addRow(["无借币数据"]);
  }
}

async function downloadWorkbook(wb, filename) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 导出账号配置 Excel：含「账号配置」与「借币配置」两个 Sheet。
 * searchParams 与列表页筛选一致（为空时即导出名下全部账号）；
 * 数据范围由后端按登录角色限定（普通用户仅本人归属账号，管理员为全部）。
 */
export async function exportAccountConfigsExcel(searchParams = {}) {
  const configs = await fetchAllAccountConfigs(searchParams);

  const wb = new ExcelJS.Workbook();
  wb.creator = "trade_volume";
  addConfigSheet(wb, configs);
  addBorrowSheet(wb, configs);

  await downloadWorkbook(wb, `账号配置_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
  return configs.length;
}
